// composables/useConfirmationOnSubmit.js
import { unref } from "vue";
import PostAddOrgDialog from "src/modules/organisations/components/PostAddOrgDialog.vue";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useQuasar } from "quasar";

// Dialog configuration constants
const DIALOG_CONFIG = {
  title: "Update Organisation",
  labelClass: "q-mx-xl rounded-borders",
  cancelLabel: "No",
  submitLabel: "Yes",
  cardStyle: { width: "600px" },
  cancellable: true,
};

// Dialog factory function
const createConfirmDialog = ($q, htmlContent, onConfirm, onCancel) => {
  return $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      ...DIALOG_CONFIG,
      htmlContent,
      onConfirm,
      onCancel,
    },
  });
};

export function useConfirmationOnSubmit({
  formState,
  formData,
  organisation,
  organisations,
  hash,
  submitForm,
  closeForm,
}) {
  const $q = useQuasar();

  const getActiveAssociatedUsers = () => {
    const currentOrganisation = unref(organisations).find(
      (org) => org._id === unref(organisation)?._id,
    );

    return (currentOrganisation?.associated_user ?? []).filter(
      (u) => u.is_active === true,
    );
  };

  const filterByRole = (users, role) => users.filter((u) => u.role === role);

  const getBusinessLine = ({ roleLabel, list }) =>
    `<p>${formData.value.name} has ${list.length} associated ${
      list.length === 1 ? roleLabel : roleLabel + "s"
    }: <br/> ${list.map((u) => u.gstin).join(", ")}.</p>`;

  const getSingleBusinessLine = ({ gstin, limitType }) =>
    `<p>${formData.value.name} has 1 associated business: ${gstin}.</p>
     <p>The increased ${limitType} limit will be applied to this business.</p>`;

  const isSingleCreditOnly = ({
    creditIncreased,
    buyers,
    sellers,
    advanceIncreased,
  }) =>
    creditIncreased &&
    buyers.length === 1 &&
    sellers.length === 0 &&
    !advanceIncreased;

  const isSingleAdvanceOnly = ({
    advanceIncreased,
    sellers,
    buyers,
    creditIncreased,
  }) =>
    advanceIncreased &&
    sellers.length === 1 &&
    buyers.length === 0 &&
    !creditIncreased;

  const buildActionLine = ({ creditIncreased, advanceIncreased }) => {
    if (creditIncreased && advanceIncreased) {
      return "Do you want to apply the increased credit and advance limit to all of them?";
    }

    const limitType = creditIncreased ? "credit" : "advance";

    return `Do you want to apply the increased ${limitType} limit to all of them?`;
  };

  const buildCreditCycleActionLine = ({ creditCycleIncreased }) => {
    const direction = creditCycleIncreased ? "increased" : "decreased";

    return `Do you want to apply the ${direction} credit days to all of them?`;
  };

  // Message builders - replace computed properties
  const buildLimitChangeMessage = () => {
    const activeUsers = getActiveAssociatedUsers();

    const creditIncreased =
      formData.value.credit_limit > (organisation.value.credit_limit || 0);
    const advanceIncreased =
      formData.value.advance_limit > (organisation.value.advance_limit || 0);

    if (!creditIncreased && !advanceIncreased) return "";

    const buyers = filterByRole(activeUsers, "buyer");
    const sellers = filterByRole(activeUsers, "seller");

    if (
      isSingleCreditOnly({ creditIncreased, buyers, sellers, advanceIncreased })
    ) {
      return getSingleBusinessLine({
        gstin: buyers[0].gstin,
        limitType: "credit",
      });
    }

    if (
      isSingleAdvanceOnly({
        advanceIncreased,
        sellers,
        buyers,
        creditIncreased,
      })
    ) {
      return getSingleBusinessLine({
        gstin: sellers[0].gstin,
        limitType: "advance",
      });
    }

    const parts = [];

    if (creditIncreased && buyers.length) {
      parts.push(getBusinessLine({ roleLabel: "customer", list: buyers }));
    }

    if (advanceIncreased && sellers.length) {
      parts.push(getBusinessLine({ roleLabel: "vendor", list: sellers }));
    }

    return `${parts.join("")}<p>${buildActionLine({
      creditIncreased,
      advanceIncreased,
    })}</p>`;
  };

  const buildCreditCycleMessage = (creditCycleIncreased, buyersList = null) => {
    const activeUsers = getActiveAssociatedUsers();
    const buyers = buyersList || filterByRole(activeUsers, "buyer");

    if (!buyers.length) return "";

    if (buyers.length === 1) {
      return `<p>${formData.value.name} has 1 associated business: ${buyers[0].gstin}.</p><p>${buildCreditCycleActionLine({ creditCycleIncreased })}</p>`;
    }

    return `${getBusinessLine({ roleLabel: "customer", list: buyers })}<p>${buildCreditCycleActionLine({ creditCycleIncreased })}</p>`;
  };

  const handleNewOrganisationSubmit = async () => {
    const submission = await submitForm(!unref(hash));

    if (!unref(hash)) {
      $q.dialog({
        component: PostAddOrgDialog,
        componentProps: {
          organisationTitle: submission.data?.data?.name,
          closeDialog: () => closeForm(true),
        },
      });
    }
  };

  const handleCycleSubmit = (originalCreditCycle, buyers) => {
    const oldCycle = Number(organisation.value.credit_cycle ?? 0);
    const newCycle = Number(originalCreditCycle ?? 0);

    const creditCycleIncreased = newCycle > oldCycle;

    createConfirmDialog(
      $q,
      buildCreditCycleMessage(creditCycleIncreased, buyers),
      async () => {
        formData.value.credit_cycle = originalCreditCycle;

        formData.value.apply_limit_to_associated = true;

        await submitForm(true);
      },
      async () => {
        formData.value.apply_limit_to_associated = false;

        await submitForm(true);
      },
    );
  };

  const handleLimitAndCycleSubmit = (
    originalCreditCycle,
    buyers,
    creditCycleChanged,
  ) => {
    createConfirmDialog(
      $q,
      buildLimitChangeMessage(),
      async () => {
        formData.value.apply_limit_to_associated = true;

        formData.value.credit_cycle = organisation.value.credit_cycle;

        await submitForm(creditCycleChanged && buyers.length ? false : true);

        if (creditCycleChanged && buyers.length) {
          handleCycleSubmit(originalCreditCycle, buyers);
        }
      },
      async () => {
        formData.value.apply_limit_to_associated = false;

        await submitForm(true);
      },
    );
  };

  const onSubmit = async () => {
    if (!unref(formState).edit) {
      await handleNewOrganisationSubmit();

      return;
    }

    const activeAssociatedUsers = getActiveAssociatedUsers();
    const isCreditLimitChanged =
      formData.value.credit_limit > organisation.value.credit_limit;
    const isAdvanceLimitChanged =
      formData.value.advance_limit > organisation.value.advance_limit;
    const oldCycle = Number(organisation.value.credit_cycle ?? 0);
    const newCycle = Number(formData.value.credit_cycle ?? 0);
    const creditCycleChanged = newCycle !== oldCycle;
    const buyers = filterByRole(activeAssociatedUsers, "buyer");
    const sellers = filterByRole(activeAssociatedUsers, "seller");

    if (
      (isCreditLimitChanged && buyers.length) ||
      (isAdvanceLimitChanged && sellers.length)
    ) {
      handleLimitAndCycleSubmit(
        formData.value.credit_cycle,
        buyers,
        creditCycleChanged,
      );
    } else if (creditCycleChanged && buyers.length) {
      handleCycleSubmit(formData.value.credit_cycle, buyers);
    } else {
      await submitForm(true);
    }
  };

  return {
    onSubmit,
  };
}
